import React, { useState } from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';

interface PlatformTrustBarProps {
  className?: string;
}

const PlatformTrustBar: React.FC<PlatformTrustBarProps> = ({ className = '' }) => {
  const config = useDomainConfig();
  const [showMore, setShowMore] = useState(false);

  // Filter and sort platforms
  const enabledPlatforms = config.supportedPlatforms
    .filter(platform => platform.enabled)
    .sort((a, b) => a.priority - b.priority);

  // Split platforms for display
  const primaryPlatforms = enabledPlatforms.slice(0, 7);
  const additionalPlatforms = enabledPlatforms.slice(7);

  const toggleMore = () => {
    setShowMore(!showMore);
  };

  return (
    <div className={`mt-6 pt-4 border-t border-gray-100 ${className}`}>
      <p className="text-center text-xs text-gray-500 mb-3">
        Supports {enabledPlatforms.length}+ platforms including:
      </p>

      {/* Primary Platforms */}
      <div className="flex justify-center items-center flex-wrap gap-3 opacity-70">
        {primaryPlatforms.map(platform => (
          <div key={platform.id} className="flex items-center space-x-1">
            {platform.id === 'xiaohongshu' ? (
              <div className="xiaohongshu-logo xiaohongshu-logo-trust" />
            ) : platform.id === 'twitter' ? (
              <span
                className="x-logo text-lg"
                style={{ color: platform.color }}
              />
            ) : (
              <span
                className={`${platform.icon} text-lg`}
                style={{ color: platform.color }}
              />
            )}
            <span className="text-xs text-gray-600 hidden sm:inline">
              {platform.displayName}
            </span>
          </div>
        ))}

        {/* Show More Button */}
        {additionalPlatforms.length > 0 && (
          <button
            onClick={toggleMore}
            className="text-xs font-medium transition-colors cursor-pointer flex items-center space-x-1 px-2 py-1 rounded-md hover:bg-gray-50"
            style={{ color: config.branding.colors.primary }}
          >
            <span>{showMore ? 'Show less' : `+${additionalPlatforms.length} more`}</span>
            <svg
              className={`w-3 h-3 transition-transform duration-200 ${showMore ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
        )}
      </div>

      {/* Additional Platforms (Expandable) */}
      {showMore && additionalPlatforms.length > 0 && (
        <div className="flex justify-center items-center flex-wrap gap-3 mt-4 opacity-70 slide-up">
          {additionalPlatforms.map(platform => (
            <div key={platform.id} className="flex items-center space-x-1">
              {platform.id === 'xiaohongshu' ? (
                <div className="xiaohongshu-logo xiaohongshu-logo-trust" />
              ) : platform.id === 'twitter' ? (
                <span
                  className="x-logo text-lg"
                  style={{ color: platform.color }}
                />
              ) : (
                <span
                  className={`${platform.icon} text-lg`}
                  style={{ color: platform.color }}
                />
              )}
              <span className="text-xs text-gray-600 hidden sm:inline">
                {platform.displayName}
              </span>
            </div>
          ))}

          {/* Future platforms placeholder */}
          <div className="flex items-center space-x-1">
            <span className="fas fa-rocket text-gray-400 text-lg" />
            <span className="text-xs text-gray-600 hidden sm:inline opacity-50">
              More Soon
            </span>
          </div>
        </div>
      )}

      {/* Brand-specific messaging */}
      {config.branding.targetAudience === 'creators' && (
        <p className="text-center text-xs text-gray-400 mt-2">
          Specialized for social media content creators
        </p>
      )}

      {config.branding.targetAudience === 'enterprise' && (
        <p className="text-center text-xs text-gray-400 mt-2">
          Professional-grade downloading for businesses
        </p>
      )}

      {config.branding.targetAudience === 'comprehensive' && (
        <p className="text-center text-xs text-gray-400 mt-2">
          Complete video and podcast downloading solution
        </p>
      )}
    </div>
  );
};

export default PlatformTrustBar;
